import {
  type ColumnDefinition,
  type ColumnLayout,
  defaultLayout,
  hiddenCount,
  reconcile,
  visibleKeys,
  withAllHidden,
  withAllVisible,
  withColumnsMoved,
  withMoved,
  withVisibility,
} from './columnConfigState';
import type { ColumnLayoutStorage } from './columnLayoutStorage';

export interface ColumnLayoutController {
  columns: readonly ColumnDefinition[];
  order: readonly string[];
  visible: Readonly<Record<string, boolean>>;
  visibleKeys: readonly string[];
  hiddenCount: number;
  columnByKey: (key: string) => ColumnDefinition | undefined;
  setVisible: (key: string, isVisible: boolean) => void;
  move: (key: string, beforeKey: string | null) => void;
  moveMany: (keys: readonly string[], beforeKey: string | null) => void;
  showAll: () => void;
  hideAll: () => void;
  reset: () => void;
}

export const loadColumnLayout = (
  tableId: string,
  columns: readonly ColumnDefinition[],
  storage: ColumnLayoutStorage,
): ColumnLayout => {
  const stored = storage.read(tableId);
  return stored === null ? defaultLayout(columns) : reconcile(stored, columns);
};

export const columnLayoutController = (
  columns: readonly ColumnDefinition[],
  layout: ColumnLayout,
  onChange: (next: ColumnLayout) => void,
): ColumnLayoutController => {
  const byKey = new Map(columns.map((column) => [column.key, column]));

  return {
    columns,
    order: layout.order,
    visible: layout.visible,
    visibleKeys: visibleKeys(layout),
    hiddenCount: hiddenCount(layout),
    columnByKey: (key) => byKey.get(key),
    setVisible: (key, isVisible) => onChange(withVisibility(layout, key, isVisible, columns)),
    move: (key, beforeKey) => onChange(withMoved(layout, key, beforeKey, columns)),
    moveMany: (keys, beforeKey) => onChange(withColumnsMoved(layout, keys, beforeKey, columns)),
    showAll: () => onChange(withAllVisible(layout)),
    hideAll: () => onChange(withAllHidden(layout, columns)),
    reset: () => onChange(defaultLayout(columns)),
  };
};
